import React, { useState } from "react";
import styled from "@emotion/styled";
import Header from "@src/components/Header";
import { Common } from "@src/styles/Common";
import ico_search from "@src/assets/ico_search_black_bold.svg";
import ico_next from "@src/assets/ico_arrow_next_black.png";
import DefaultProfileImg from "@src/assets/ApproveWait.png";
import { useGetExpertList } from "./service/expertSearch.query";
import { useDebounce } from "@src/hooks/useDebounce";
import { transferToNative } from "@src/lib/WebAppBridge";

const CATEGORIES = ["전체", "중개", "매물컨설팅", "매수신청대리", "임장동행"];

const ExpertSearch = () => {
    const [selectedCategory, setSelectedCategory] = useState("전체");
    const [searchText, setSearchText] = useState("");
    const debouncedSearchText = useDebounce(searchText, 300);

    const serviceTypeMap: Record<string, 1 | 2 | 3 | 4 | undefined> = {
        전체: undefined,
        중개: 1,
        매수신청대리: 2,
        매물컨설팅: 3,
        임장동행: 4,
    };

    const queryParams = {
        ...(debouncedSearchText ? { expertName: debouncedSearchText } : {}),
        ...(serviceTypeMap[selectedCategory] ? { serviceType: serviceTypeMap[selectedCategory] } : {}),
    };

    const { data: expertListData } = useGetExpertList(queryParams);
    const topExperts = expertListData?.topExperts || [];

    const getExpertCategoryAndExp = (expert: any) => {
        if (selectedCategory !== "전체") {
            let career = 0;
            if (selectedCategory === "중개") career = expert.agentCareer;
            if (selectedCategory === "매수신청대리") career = expert.auctionAgentCareer;
            if (selectedCategory === "매물컨설팅") career = expert.consultingCareer;
            if (selectedCategory === "임장동행") career = expert.companionCareer;
            return { category: selectedCategory, exp: `${career}년` };
        }

        const careers = [
            { name: "매수신청대리", value: expert.auctionAgentCareer },
            { name: "매물컨설팅", value: expert.consultingCareer },
            { name: "임장동행", value: expert.companionCareer },
            { name: "중개", value: expert.agentCareer },
        ];
        const validCareers = careers.filter((c) => c.value > 0).sort((a, b) => b.value - a.value);

        if (validCareers.length > 0) {
            return { category: validCareers[0].name, exp: `${validCareers[0].value}년` };
        }

        return { category: "전문가", exp: `${expert.experience}년` };
    };

    const handleClickExpert = (expertUserNo: number, expertNo: number) => {
        transferToNative(JSON.stringify({ expertUserNo, expertNo }), "onClickExpert");
    };

    return (
        <Container>
            <div>
                <Header title="전문가 검색" />
            </div>

            <SearchSection>
                <SearchInputWrapper>
                    <img src={ico_search} alt="search" className="search-icon" />
                    <input
                        type="text"
                        placeholder="전문가 검색"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                </SearchInputWrapper>
            </SearchSection>

            <FilterSection>
                {CATEGORIES.map((category) => (
                    <FilterChip
                        key={category}
                        $active={selectedCategory === category}
                        onClick={() => setSelectedCategory(category)}
                    >
                        {category}
                    </FilterChip>
                ))}
            </FilterSection>

            <TotalCount>
                총 <span>{topExperts.length}</span> 명
            </TotalCount>

            <ListSection>
                {topExperts.map((expert) => {
                    const { category, exp } = getExpertCategoryAndExp(expert);

                    return (
                        <ExpertItem key={expert.expertNo} onClick={() => handleClickExpert(expert.expertUserNo, expert.expertNo)}>
                            <ItemTop>
                                <img className="profile" src={expert.profile || DefaultProfileImg} alt="profile" />
                                <div className="info">
                                    <span className="name">{expert.name}</span>
                                    <span className="company">{expert.officeInfo?.name}</span>
                                </div>
                                <img className="next" src={ico_next} alt="next" />
                            </ItemTop>

                            <ItemMiddle>
                                <Badge>경력</Badge>
                                <span className="exp-text">
                                    {category} {exp}
                                </span>
                            </ItemMiddle>

                            {expert.introduction && <ItemBottom>{expert.introduction}</ItemBottom>}
                        </ExpertItem>
                    );
                })}
            </ListSection>
        </Container>
    );
};

export default ExpertSearch;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    height: 100vh;
    background-color: ${Common.colors.white};
`;

const SearchSection = styled.div`
    padding: 10px 12px 10px 12px;
    background-color: ${Common.colors.white};
`;

const SearchInputWrapper = styled.div`
    display: flex;
    align-items: center;
    border: 1px solid ${Common.colors.gray300};
    border-radius: 8px;
    padding: 0 16px;
    height: 48px;

    .search-icon {
        width: 20px;
        height: 20px;
        margin-right: 8px;
    }

    input {
        flex: 1;
        border: none;
        outline: none;
        font-size: 16px;
        color: ${Common.colors.black};
        ${Common.textStyle.body2_R16};

        &::placeholder {
            color: ${Common.colors.gray400};
        }
    }
`;

const FilterSection = styled.div`
    display: flex;
    gap: 8px;
    padding: 10px 12px 10px 12px;
    overflow-x: auto;
    white-space: nowrap;
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
`;

const FilterChip = styled.button<{ $active: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 36px;
    padding: 0 16px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    ${({ $active }) =>
        $active
            ? `
        background-color: ${Common.colors.blue};
        color: ${Common.colors.white};
        border: 1px solid ${Common.colors.blue};
    `
            : `
        background-color: ${Common.colors.white};
        color: ${Common.colors.black};
        border: 1px solid ${Common.colors.gray200};
    `}
`;

const TotalCount = styled.div`
    padding: 10px 12px 10px 12px;
    ${Common.textStyle.body1_M16};
    color: ${Common.colors.black};

    span {
        color: ${Common.colors.black};
    }
`;

const ListSection = styled.div`
    display: flex;
    flex-direction: column;
    height: 70vh;
    overflow: scroll;
`;

const ExpertItem = styled.div`
    display: flex;
    flex-direction: column;
    padding: 24px 20px;
    border-top: 1px solid ${Common.colors.gray100};
    background-color: ${Common.colors.white};

    &:last-child {
        border-bottom: 1px solid ${Common.colors.gray100};
    }
`;

const ItemTop = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 12px;

    .profile {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        object-fit: cover;
        margin-right: 12px;
        background-color: ${Common.colors.gray100};
    }

    .info {
        flex: 1;
        display: flex;
        flex-direction: column;

        .name {
            ${Common.textStyle.display1_B18};
            color: ${Common.colors.navyPrimary};
        }

        .company {
            ${Common.textStyle.title6_R14};
            color: ${Common.colors.gray600};
            margin-top: 2px;
        }
    }

    .next {
        width: 20px;
        height: 20px;
        cursor: pointer;
    }
`;

const ItemMiddle = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    gap: 8px;

    .exp-text {
        ${Common.textStyle.body2_R16};
        color: ${Common.colors.black};
    }
`;

const Badge = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 24px;
    padding: 0 8px;
    border-radius: 4px;
    border: 1px solid ${Common.colors.blue};
    color: ${Common.colors.blue};
    ${Common.textStyle.title8_R12};
    background-color: transparent;
`;

const ItemBottom = styled.div`
    background-color: ${Common.colors.gray100};
    border-radius: 8px;
    padding: 16px;
    ${Common.textStyle.body3_R14};
    color: ${Common.colors.black};
    line-height: 1.5;
`;
